<template>
  <div class="sun-tracker-editor space-y-5">
    <!-- Header -->
    <div class="sun-tracker-editor__header">
      <div>
        <h3 class="text-sm font-semibold text-gray-900 dark:text-white">
          {{ tt('Sun Controller', 'Sončni krmilnik') }}
        </h3>
        <p class="text-xs text-gray-500 dark:text-gray-400">
          {{
            tt(
              'Season × Occupancy — each cell has a base position, events and optional sun tracking. Security / privacy positions are handled by scenes.',
              'Letni čas × prisotnost — vsaka celica ima osnovni položaj, dogodke in neobvezno sončno vodenje. Varnostni / zasebnostni položaji so urejeni s scenami.',
            )
          }}
        </p>
      </div>
      <div class="sun-tracker-editor__header-actions">
        <button
          type="button"
          class="sun-tracker-editor__preset"
          :title="tt('Reset to recommended settings', 'Ponastavi na priporočene nastavitve')"
          @click="applyPreset">
          <ControlIcon name="sparkles" size="1rem" />
          {{ tt('Recommended', 'Priporočeno') }}
        </button>
        <label
          class="sun-tracker-editor__switch"
          :title="tt('Enable / disable controller', 'Omogoči / onemogoči krmilnik')">
          <input
            type="checkbox"
            class="sr-only peer"
            :checked="config.enabled"
            @change="setEnabled(($event.target as HTMLInputElement).checked)" />
          <div
            class="w-9 h-5 bg-gray-200 dark:bg-gray-600 peer-checked:bg-amber-500 rounded-full relative after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4"></div>
        </label>
      </div>
    </div>

    <!-- Live preview -->
    <div class="sun-tracker-editor__live" :class="{ 'sun-tracker-editor__live--active': preview.source !== 'none' }">
      <div class="sun-tracker-editor__live-row">
        <ControlIcon name="warm" size="1.125rem" />
        <span class="sun-tracker-editor__live-item">
          {{ tt('Azimuth', 'Azimut') }} <b>{{ liveAzimuth }}</b>
        </span>
        <span class="sun-tracker-editor__live-item">
          {{ tt('Altitude', 'Višina') }} <b>{{ liveAltitude }}</b>
        </span>
        <span class="sun-tracker-editor__live-item">
          {{ tt('Rise', 'Vzhod') }} <b>{{ liveSunrise }}</b>
        </span>
        <span class="sun-tracker-editor__live-item">
          {{ tt('Set', 'Zahod') }} <b>{{ liveSunset }}</b>
        </span>
      </div>
      <div class="sun-tracker-editor__live-row">
        <span class="sun-tracker-editor__badge" :class="resolvedSeason ? 'sun-tracker-editor__badge--on' : ''">
          {{ tt('Season', 'Letni čas') }}: {{ resolvedSeasonLabel }}
        </span>
        <span class="sun-tracker-editor__badge" :class="preview.occupancy ? 'sun-tracker-editor__badge--on' : ''">
          {{ tt('Occupancy', 'Prisotnost') }}:
          {{ preview.occupancy ? sunLabel(SUN_OCCUPANCY_LABELS[preview.occupancy]) : '—' }}
        </span>
        <span
          class="sun-tracker-editor__badge"
          :class="preview.source !== 'none' ? 'sun-tracker-editor__badge--on' : ''">
          {{ tt('Source', 'Vir') }}: {{ sunLabel(SUN_SOURCE_LABELS[preview.source]) }}
        </span>
        <span class="sun-tracker-editor__badge" :class="preview.sunInWindow ? 'sun-tracker-editor__badge--on' : ''">
          {{ tt('Sun in window', 'Sonce v oknu') }}: {{ preview.sunInWindow ? tt('yes', 'da') : tt('no', 'ne') }}
        </span>
      </div>
      <div v-if="preview.source !== 'none' && preview.position != null" class="sun-tracker-editor__live-row">
        <span class="sun-tracker-editor__live-item">
          {{ tt('Target position', 'Ciljni položaj') }} <b>{{ preview.position }}%</b>
          <template v-if="preview.tilt != null">
            · {{ tt('tilt', 'naklon') }} <b>{{ preview.tilt }}%</b></template
          >
        </span>
      </div>
    </div>

    <!-- Season -->
    <section class="sun-tracker-editor__section">
      <h4 class="sun-tracker-editor__section-title">{{ tt('Season', 'Letni čas') }}</h4>
      <div class="sun-tracker-editor__season">
        <div class="sun-tracker-editor__segmented">
          <button
            type="button"
            :class="{ 'sun-tracker-editor__segment--active': config.season_mode === 'auto' }"
            @click="updateGlobal({ season_mode: 'auto' })">
            {{ tt('Auto', 'Samodejno') }}
          </button>
          <button
            v-for="season in seasonTabs"
            :key="season.id"
            type="button"
            :class="{ 'sun-tracker-editor__segment--active': config.season_mode === season.id }"
            @click="updateGlobal({ season_mode: season.id })">
            {{ season.name }}
          </button>
        </div>
        <div v-if="config.season_mode === 'auto'" class="sun-tracker-editor__climate">
          <ControlIcon name="eco" size="0.875rem" />
          <span>
            {{ tt('Shades season', 'Sezona senčil') }}: <b>{{ activeSeasonName }}</b>
          </span>
        </div>
      </div>
    </section>

    <!-- Sleep window -->
    <section class="sun-tracker-editor__section">
      <h4 class="sun-tracker-editor__section-title">{{ tt('Sleep window', 'Okno spanja') }}</h4>
      <p class="sun-tracker-editor__section-desc">
        {{
          tt(
            'While present inside this window the “Sleep” cell applies instead of “Presence”. The window wraps overnight (e.g. 22:00 → 07:00).',
            'Ko je oseba prisotna znotraj tega okna, velja celica »Spanje« namesto »Prisotnost«. Okno se prevesi čez polnoč (npr. 22:00 → 07:00).',
          )
        }}
      </p>
      <div class="sun-tracker-editor__sleep-grid">
        <SunAnchorEditor
          :label="tt('Start', 'Začetek')"
          :model-value="config.sleep_window.start"
          @update:model-value="updateSleep('start', $event)" />
        <SunAnchorEditor
          :label="tt('End', 'Konec')"
          :model-value="config.sleep_window.end"
          @update:model-value="updateSleep('end', $event)" />
      </div>
    </section>

    <!-- Sun window -->
    <section class="sun-tracker-editor__section">
      <h4 class="sun-tracker-editor__section-title">{{ tt('Sun window', 'Sončno okno') }}</h4>
      <p class="sun-tracker-editor__section-desc">
        {{
          tt(
            'The sun window is the range of sun positions that hit this facade. Drag the amber handles to adjust, or type the values.',
            'Sončno okno je razpon položajev sonca, ki osvetljujejo to fasado. Jantarna ročaja povlecite za prilagoditev ali vnesite vrednosti.',
          )
        }}
      </p>
      <div class="sun-tracker-editor__window-grid">
        <SunWindowCompass
          :min="config.sun_window.azimuth_min"
          :max="config.sun_window.azimuth_max"
          :sun-azimuth="sunPosition?.azimuth ?? null"
          @update:min="updateSunWindow({ azimuth_min: $event })"
          @update:max="updateSunWindow({ azimuth_max: $event })" />
        <SunWindowArc
          :min="config.sun_window.altitude_min"
          :sun-altitude="sunPosition?.altitude ?? null"
          :sun-progress="sunArcProgress"
          @update:min="updateSunWindow({ altitude_min: $event })" />
      </div>
      <div class="sun-tracker-editor__window-numbers">
        <label class="sun-tracker-editor__number">
          <span>{{ tt('Azimuth min', 'Azimut min') }}</span>
          <input
            type="number"
            min="0"
            max="360"
            :value="config.sun_window.azimuth_min"
            @change="
              updateSunWindow({ azimuth_min: clampNum(Number(($event.target as HTMLInputElement).value), 0, 360) })
            " />
          <span>°</span>
        </label>
        <label class="sun-tracker-editor__number">
          <span>{{ tt('Azimuth max', 'Azimut max') }}</span>
          <input
            type="number"
            min="0"
            max="360"
            :value="config.sun_window.azimuth_max"
            @change="
              updateSunWindow({ azimuth_max: clampNum(Number(($event.target as HTMLInputElement).value), 0, 360) })
            " />
          <span>°</span>
        </label>
        <label class="sun-tracker-editor__number">
          <span>{{ tt('Min altitude', 'Najnižja višina') }}</span>
          <input
            type="number"
            min="0"
            max="90"
            :value="config.sun_window.altitude_min"
            @change="
              updateSunWindow({ altitude_min: clampNum(Number(($event.target as HTMLInputElement).value), 0, 90) })
            " />
          <span>°</span>
        </label>
      </div>
    </section>

    <!-- Cells -->
    <section v-for="season in seasonTabs" :key="season.id" class="sun-tracker-editor__section">
      <h4 class="sun-tracker-editor__section-title">
        <!-- The season's icon is the user's own pick: `season.icon` is a
             free-text Material name entered in the Shades settings
             (`SettingsShades.vue`'s season editor) — user data, not an
             affordance glyph, so it renders as the user wrote it (the
             scan-control-glyphs.cjs exception note, card t_a8249a54). -->
        <span v-if="season.icon" class="material-symbols-outlined text-base leading-none align-middle">{{
          season.icon
        }}</span>
        {{ season.name }}
      </h4>
      <div class="sun-tracker-editor__cells">
        <SunCellCard
          v-for="occupancy in SUN_OCCUPANCY_KEYS"
          :key="occupancy"
          :occupancy="occupancy"
          :model-value="resolveCellForSeason(config.cells, season.id)[occupancy]"
          @update:model-value="updateCell(season.id, occupancy, $event)" />
      </div>
    </section>

    <!-- Advanced -->
    <section class="sun-tracker-editor__section">
      <button type="button" class="sun-tracker-editor__advanced-toggle" @click="advancedOpen = !advancedOpen">
        <ControlIcon :name="advancedOpen ? 'chevron-up' : 'chevron-down'" size="1rem" />
        {{ tt('Advanced', 'Napredno') }}
      </button>

      <div v-if="advancedOpen" class="sun-tracker-editor__advanced space-y-3">
        <label class="sun-tracker-editor__check">
          <input
            type="checkbox"
            :checked="config.default_position != null"
            @change="onDefaultPositionToggle(($event.target as HTMLInputElement).checked)" />
          <span>{{
            tt('Move to a default position when nothing is active', 'Premakni na privzeti položaj, ko ni nič aktivno')
          }}</span>
        </label>
        <div v-if="config.default_position != null" class="sun-tracker-editor__slider">
          <div class="sun-tracker-editor__slider-head">
            <span class="text-xs text-gray-500 dark:text-gray-400">{{
              tt('Default position', 'Privzeti položaj')
            }}</span>
            <span class="sun-tracker-editor__slider-value">{{ config.default_position }}%</span>
          </div>
          <input
            type="range"
            min="0"
            max="100"
            :value="config.default_position"
            @input="updateGlobal({ default_position: Number(($event.target as HTMLInputElement).value) })" />
        </div>

        <div class="sun-tracker-editor__advanced-grid">
          <label class="sun-tracker-editor__number">
            <span>{{ tt('Brightness hysteresis', 'Histereza svetlosti') }}</span>
            <input
              type="number"
              min="0"
              step="500"
              :value="config.brightness_hysteresis_lux"
              @change="
                updateGlobal({ brightness_hysteresis_lux: Number(($event.target as HTMLInputElement).value) || 0 })
              " />
            <span>lux</span>
          </label>
          <label class="sun-tracker-editor__number">
            <span>{{ tt('Activation delay', 'Zakasnitev vklopa') }}</span>
            <input
              type="number"
              min="0"
              step="10"
              :value="config.delay_on_seconds"
              @change="updateGlobal({ delay_on_seconds: Number(($event.target as HTMLInputElement).value) || 0 })" />
            <span>{{ tt('sec', 'sek') }}</span>
          </label>
          <label class="sun-tracker-editor__number">
            <span>{{ tt('Release delay', 'Zakasnitev izklopa') }}</span>
            <input
              type="number"
              min="0"
              step="30"
              :value="config.delay_off_seconds"
              @change="updateGlobal({ delay_off_seconds: Number(($event.target as HTMLInputElement).value) || 0 })" />
            <span>{{ tt('sec', 'sek') }}</span>
          </label>
          <label class="sun-tracker-editor__number">
            <span>{{ tt('Priority', 'Prioriteta') }}</span>
            <input
              type="number"
              min="1"
              max="100"
              :value="config.priority ?? 50"
              @change="updateGlobal({ priority: Number(($event.target as HTMLInputElement).value) || 50 })" />
            <span></span>
          </label>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { formatTime as formatTimeLocale } from '../../../utils/formatDate';
import { useSolar } from '../../../composables/useSolar';
import { useClimateStore } from '../../../stores/climate';
import { useShadesStore } from '../../../stores/shades';
import {
  SUN_OCCUPANCY_KEYS,
  SUN_OCCUPANCY_LABELS,
  SUN_SEASON_LABELS,
  SUN_SOURCE_LABELS,
  SunCellConfig,
  SunOccupancyKey,
  SunSeasonKey,
  SunTrackerConfig,
  SunWindowAnchor,
  sunLabel,
  computeActiveCell,
  getSunTrackerDefaults,
  normalizeSunTrackerConfig,
  resolveCellForSeason,
} from '../../../utils/sunTracker';
import type { HubConfigBag } from '../deviceWidgetTypes';
import SunWindowArc from './SunWindowArc.vue';
import SunWindowCompass from './SunWindowCompass.vue';
import SunCellCard from './SunCellCard.vue';
import SunAnchorEditor from './SunAnchorEditor.vue';
import ControlIcon from '../../device-icons/ControlIcon.vue';
import { tt } from '../../../utils/consoleText';

const props = defineProps<{
  modelValue: HubConfigBag;
  /** Live presence value from the automation binding (optional preview) */
  presenceValue?: boolean;
  /** Live brightness value from the automation binding (optional preview) */
  brightnessValue?: number;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: HubConfigBag): void;
}>();

const advancedOpen = ref(false);

const solar = useSolar();
const climate = useClimateStore();
const shades = useShadesStore();
const sunPosition = computed(() => solar.sunPosition.value);
const sunTimes = computed(() => solar.sunTimes.value);
const sunArcProgress = computed(() => solar.sunArcProgress.value);

onMounted(() => {
  if (shades.seasonsSorted.length === 0) {
    void shades.fetchSeasons();
  }
  // Legacy fallback — used only while the hub has no Shades seasons stored.
  if (!climate.activeSeasonKey) {
    void climate.resolveActiveSeason();
  }
});

const config = computed<SunTrackerConfig>(() => normalizeSunTrackerConfig(props.modelValue));

/** The season tabs: the Shades category's custom seasons (legacy pair as fallback). */
const seasonTabs = computed<Array<{ id: string; name: string; icon: string }>>(() => {
  if (shades.seasonsSorted.length > 0) {
    return shades.seasonsSorted.map((season) => ({ id: season.id, name: season.name, icon: season.icon }));
  }
  return [
    { id: 'summer', name: sunLabel(SUN_SEASON_LABELS.summer), icon: 'wb_sunny' },
    { id: 'winter', name: sunLabel(SUN_SEASON_LABELS.winter), icon: 'ac_unit' },
  ];
});

const seasonLabels = computed<Record<string, string>>(() =>
  Object.fromEntries(seasonTabs.value.map((season) => [season.id, season.name])),
);

const legacyClimateSeasonLabel = computed(() => {
  const key = climate.activeSeasonKey;
  if (key === 'SUMMER') return tt('Summer', 'Poletje');
  if (key === 'WINTER') return tt('Winter', 'Zima');
  if (key === 'TRANSITIONAL') return tt('Transitional', 'Prehodno');
  return '—';
});

const activeSeasonName = computed(() => {
  if (shades.seasonsSorted.length > 0) return shades.activeSeason?.name ?? '—';
  return legacyClimateSeasonLabel.value;
});

const resolvedSeason = computed<SunSeasonKey | null>(() => {
  if (config.value.season_mode !== 'auto') return config.value.season_mode;
  if (shades.seasonsSorted.length > 0) return shades.activeSeasonId;
  // Legacy fallback (no Shades seasons stored yet) — the hub resolves shade
  // seasons from the Climate ones in that case.
  const key = climate.activeSeasonKey;
  if (key === 'SUMMER') return 'summer';
  if (key === 'WINTER') return 'winter';
  if (key === 'TRANSITIONAL') return config.value.transitional_season;
  return null;
});

const resolvedSeasonLabel = computed(() => {
  const season = resolvedSeason.value;
  if (!season) return '—';
  if (seasonLabels.value[season]) return seasonLabels.value[season];
  const legacy = SUN_SEASON_LABELS[season];
  return legacy ? sunLabel(legacy) : season;
});

const liveAzimuth = computed(() => (sunPosition.value ? `${Math.round(sunPosition.value.azimuth)}°` : '—'));
const liveAltitude = computed(() => (sunPosition.value ? `${Math.round(sunPosition.value.altitude)}°` : '—'));
const liveSunrise = computed(() => (sunTimes.value ? formatTime(sunTimes.value.sunrise) : '—'));
const liveSunset = computed(() => (sunTimes.value ? formatTime(sunTimes.value.sunset) : '—'));

const preview = computed(() =>
  computeActiveCell(
    config.value,
    resolvedSeason.value,
    sunPosition.value
      ? {
          azimuth: sunPosition.value.azimuth,
          altitude: sunPosition.value.altitude,
          brightness: props.brightnessValue,
          occupied: props.presenceValue ?? true,
        }
      : null,
  ),
);

function commit(next: SunTrackerConfig) {
  emit('update:modelValue', JSON.parse(JSON.stringify(next)) as HubConfigBag);
}

function updateGlobal(patch: Partial<SunTrackerConfig>) {
  commit({ ...config.value, ...patch });
}

function updateSunWindow(patch: Partial<SunTrackerConfig['sun_window']>) {
  commit({
    ...config.value,
    sun_window: { ...config.value.sun_window, ...patch },
  });
}

function updateSleep(side: 'start' | 'end', anchor: SunWindowAnchor) {
  commit({
    ...config.value,
    sleep_window: { ...config.value.sleep_window, [side]: anchor },
  });
}

function updateCell(season: SunSeasonKey, occupancy: SunOccupancyKey, cell: SunCellConfig) {
  const seasonCells = resolveCellForSeason(config.value.cells, season);
  commit({
    ...config.value,
    cells: {
      ...config.value.cells,
      [season]: {
        ...seasonCells,
        [occupancy]: cell,
      },
    },
  });
}

function setEnabled(enabled: boolean) {
  updateGlobal({ enabled });
}

function applyPreset() {
  commit(getSunTrackerDefaults());
}

function onDefaultPositionToggle(checked: boolean) {
  updateGlobal({ default_position: checked ? 0 : null });
}

function clampNum(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}

function formatTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return formatTimeLocale(date, { hour: '2-digit', minute: '2-digit' });
}
</script>

<style scoped>
.sun-tracker-editor {
  width: 100%;
}

.sun-tracker-editor__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.sun-tracker-editor__header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.sun-tracker-editor__preset {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: rgb(245, 158, 11);
  background: rgba(245, 158, 11, 0.1);
  border-radius: 999px;
  padding: 7px 12px;
}

.sun-tracker-editor__section-title {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: rgb(100, 116, 139);
  margin-bottom: 8px;
}

.sun-tracker-editor__section-desc {
  font-size: 11px;
  line-height: 1.45;
  color: rgb(148, 163, 184);
  margin-bottom: 10px;
}

.sun-tracker-editor__season {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sun-tracker-editor__segmented {
  display: inline-flex;
  gap: 4px;
  background: rgba(148, 163, 184, 0.12);
  border-radius: 999px;
  padding: 3px;
  align-self: flex-start;
}

.sun-tracker-editor__segmented button {
  font-size: 12px;
  font-weight: 600;
  padding: 5px 14px;
  border-radius: 999px;
  color: rgb(148, 163, 184);
}

.sun-tracker-editor__segment--active {
  background: rgb(245, 158, 11);
  color: #fff !important;
}

.sun-tracker-editor__climate {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: rgb(107, 114, 128);
  background: rgba(16, 185, 129, 0.08);
  border: 1px solid rgba(16, 185, 129, 0.25);
  border-radius: 10px;
  padding: 8px 10px;
}

.sun-tracker-editor__climate select {
  background: transparent;
  color: inherit;
  font-size: 12px;
  font-weight: 600;
  border-radius: 6px;
  padding: 2px 4px;
}

.sun-tracker-editor__sleep-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.sun-tracker-editor__window-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.sun-tracker-editor__window-numbers {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-top: 10px;
}

.sun-tracker-editor__cells {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 10px;
}

.sun-tracker-editor__advanced-toggle {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: rgb(100, 116, 139);
}

.sun-tracker-editor__advanced-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.sun-tracker-editor__number {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: rgb(148, 163, 184);
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 8px;
  padding: 6px 8px;
}

.sun-tracker-editor__number input {
  flex: 1;
  min-width: 0;
  background: transparent;
  color: inherit;
  font-size: 12px;
  text-align: right;
}

.sun-tracker-editor__slider-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sun-tracker-editor__slider input[type='range'] {
  width: 100%;
  accent-color: rgb(245, 158, 11);
}

.sun-tracker-editor__slider-value {
  font-size: 11px;
  font-weight: 600;
}

.sun-tracker-editor__check {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: rgb(107, 114, 128);
  cursor: pointer;
}

.sun-tracker-editor__check input {
  accent-color: rgb(245, 158, 11);
}

/* Live preview */
.sun-tracker-editor__live {
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.02);
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: border-color 0.2s;
}

.sun-tracker-editor__live--active {
  border-color: rgba(245, 158, 11, 0.5);
}

.sun-tracker-editor__live-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 14px;
}

.sun-tracker-editor__live-item {
  font-size: 12px;
  color: rgb(107, 114, 128);
}

.sun-tracker-editor__live-item b {
  color: rgb(245, 158, 11);
  font-variant-numeric: tabular-nums;
}

.sun-tracker-editor__badge {
  font-size: 11px;
  font-weight: 600;
  color: rgb(148, 163, 184);
  background: rgba(148, 163, 184, 0.1);
  border-radius: 999px;
  padding: 4px 10px;
}

.sun-tracker-editor__badge--on {
  color: rgb(245, 158, 11);
  background: rgba(245, 158, 11, 0.12);
}
</style>
